import React, { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogContent, IconButton, Switch, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import { TOKENS } from "./tokens";
import { styled } from "@mui/material/styles";
import {
    cookiesAcceptAll,
    cookiesCloseSettings,
    cookiesRejectNonEssential,
    cookiesSavePreferences
} from "../../../actions/cookies/actions";
import Immutable from "immutable";
import {useTranslation} from "react-i18next";
import {FOOTER} from "../../../locales/components/namespaces";


const BrwSwitch = styled(Switch)(() => ({
    width: 51.33,
    height: 28,
    padding: 0,
    "& .MuiSwitch-switchBase": {
        padding: 2.33,
        "&.Mui-checked": {
            transform: "translateX(23px)",
            "& + .MuiSwitch-track": {
                backgroundColor: "#E5007E",
                borderColor: "#E5007E",
                opacity: 1,
            },
            "& .MuiSwitch-thumb": {
                width: 23.33,
                height: 23.33,
                boxShadow: "none",
                backgroundColor: "#FCFCFC",
            },
        },
    },
    "& .MuiSwitch-thumb": {
        width: 23.33,
        height: 23.33,
        boxShadow: "none",
        backgroundColor: TOKENS.black,
    },
    "& .MuiSwitch-track": {
        borderRadius: 33.0758,
        backgroundColor: "#FCFCFC",
        opacity: 1,
        border: "0.893939px solid #111111",
        boxSizing: "border-box",
    },
}));

function Row({ title, right, desc }) {
    return (
        <Box sx={{ py: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography sx={{ flex: 1, fontFamily: "Roboto Condensed", fontWeight: 700, fontSize: 20, color: TOKENS.black }}>
                    {title}
                </Typography>
                {right}
            </Box>
            <Typography sx={{ fontFamily: "Roboto Condensed", fontWeight: 400, fontSize: 16, lineHeight: "100%", color: TOKENS.black, mt: 0 }}>
                {desc}
            </Typography>
        </Box>
    );
}

export default function CookieSettingsDialog() {
    const dispatch = useDispatch();
    const { t} = useTranslation(FOOTER);
    const settingsOpen = useSelector((s) => !!s.cookies?.get("settingsOpen"));
    const consent = useSelector((s) => s.cookies?.get("consent"));
    const consentJS = Immutable.Map.isMap(consent) ? consent.toJS() : consent;
    const [performance, setPerformance] = useState(false);
    const [marketing, setMarketing] = useState(false);

    useEffect(() => {
        setPerformance(!!consentJS?.performance);
        setMarketing(!!consentJS?.marketing);
    }, [settingsOpen, consentJS?.performance, consentJS?.marketing]);

    const onSave = () => {
        dispatch(cookiesSavePreferences({ performance, marketing }));
        dispatch(cookiesCloseSettings());
    };

    return (
        <Dialog
            open={settingsOpen}
            onClose={() => dispatch(cookiesCloseSettings())}
            PaperProps={{
                sx: {
                    width: { xs: "95vw", sm: TOKENS.w },
                    height: { xs: "auto", sm: TOKENS.h },
                    borderRadius: 6,
                    bgcolor: TOKENS.white,
                    border: `1px solid ${TOKENS.black}`,
                },
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2, pb: 0 }}>
                        <Typography
                            sx={{
                                flex: 1,
                                fontFamily: "Zen Maru Gothic",
                                fontWeight: 900,
                                fontSize: 24,
                                lineHeight: "110%",
                                textTransform: "uppercase",
                                color: TOKENS.black,
                            }}
                        >
                            {t('Cookie Settings')}
                        </Typography>

                        <IconButton
                            onClick={() => dispatch(cookiesCloseSettings())}
                            sx={{ width: 60, height: 60,borderRadius: 10, "&:hover": { bgcolor: TOKENS.lightGray,borderRadius: 10 } }}
                        >
                            <CloseIcon sx={{ color: TOKENS.black }} />
                        </IconButton>
                    </Box>

                    <Box sx={{ borderTop: `1px solid ${TOKENS.black}` }} />

                    <Typography sx={{ fontFamily: "Roboto Condensed", fontSize: 16, lineHeight: "120%", color: TOKENS.black }}>
                        {t('Customize consent preferences. Functional cookies are always active. You can enable performance and marketing cookies.')}
                    </Typography>

                    <Box sx={{ borderTop: `1px solid ${TOKENS.black}` }} />

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
                        <Row
                            title="Functional"
                            right={
                                <Typography sx={{ fontFamily: "Roboto Condensed", fontWeight: 700, fontSize: 14, textTransform: "uppercase", color: TOKENS.magenta }}>
                                    {t('Always active')}
                                </Typography>
                            }
                            desc={t("Required cookies to make the website work properly.")}
                        />
                        <Box sx={{ borderTop: `1px solid ${TOKENS.black}` }} />

                        <Row
                            title="Performance"
                            right={<BrwSwitch checked={performance} onChange={(e) => setPerformance(e.target.checked)} />}
                            desc={t("Analytics cookies that help us improve the website.")}
                        />
                        <Box sx={{ borderTop: `1px solid ${TOKENS.black}` }} />

                        <Row
                            title="Marketing"
                            right={<BrwSwitch checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />}
                            desc={t("Marketing cookies for personalized advertising.")}
                        />
                    </Box>

                    <Box sx={{ display: "flex", gap: 1.25, pt: 1, flexWrap: "wrap",mt:5 }}>
                        <Button
                            onClick={() => { dispatch(cookiesAcceptAll()); dispatch(cookiesCloseSettings()); }}
                            variant="contained"
                            sx={{
                                flex: 1,
                                minWidth: 100,
                                minHeight: 37,
                                height: "auto",
                                borderRadius: 120,
                                bgcolor: TOKENS.magenta,
                                border: `1px solid ${TOKENS.magenta}`,
                                fontFamily: "Zen Maru Gothic",
                                fontWeight: 900,
                                fontSize: 14,
                                textTransform: "none",
                            }}
                        >
                            {t('Accept all')}
                        </Button>

                        <Button
                            onClick={() => { dispatch(cookiesRejectNonEssential()); dispatch(cookiesCloseSettings()); }}
                            variant="outlined"
                            sx={{
                                flex: 1,
                                minWidth: 100,
                                minHeight: 37,
                                height: "auto",
                                borderRadius: 120,
                                borderColor: TOKENS.magenta,
                                color: TOKENS.magenta,
                                fontFamily: "Zen Maru Gothic",
                                fontWeight: 900,
                                fontSize: 14,
                                textTransform: "none",
                            }}
                        >
                            {t('Reject non-essential')}
                        </Button>

                        <Button
                            onClick={onSave}
                            variant="outlined"
                            sx={{
                                flex: 1,
                                minWidth: 100,
                                minHeight: 37,
                                height: "auto",
                                borderRadius: 120,
                                borderColor: TOKENS.magenta,
                                color: TOKENS.magenta,
                                fontFamily: "Zen Maru Gothic",
                                fontWeight: 900,
                                fontSize: 14,
                                textTransform: "none",
                            }}
                        >
                            {t('Save preferences')}
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
